import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Card, CardContent, FormControl, InputLabel, Select, MenuItem, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Tooltip, Grid, Alert, CircularProgress, } from "@mui/material";
import { Visibility as VisibilityIcon, CalendarMonth as CalendarIcon, } from "@mui/icons-material";
import { getData } from "../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../services/endpoints";
import PayslipPreviewView from "./payslipPreviewView";


const MONTHS = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

const EmployeePayslips = () => {
    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const userId = userData?.id;
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    const [startMonth, setStartMonth] = useState(currentMonth);
    const [startYear, setStartYear] = useState(currentYear);
    const [endMonth, setEndMonth] = useState(currentMonth);
    const [endYear, setEndYear] = useState(currentYear);
    const [payslips, setPayslips] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [selectedPayslip, setSelectedPayslip] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);

    const fetchPayslips = useCallback(async () => {
        setLoading(true);
        setError("");

        try {
            const response = await getData(API_ENDPOINTS.getEmployeePayslipByDateRange(
                startMonth + 1,
                startYear,
                endMonth + 1,
                endYear,
                userId
            ));

            if (response.statusCode === 200 && response.result?.employees?.length > 0) {
                const employeeData = response.result.employees[0];
                const mappedPayslips = (employeeData.payslips || []).map(p => ({
                    ...p,
                    month: MONTHS.indexOf(p.month) + 1,
                    grossSalary: parseFloat(p.earnings?.grossMonthly || 0),
                    totalDeductions: parseFloat(p.deductions?.totalDeductions || 0),
                    netSalary: parseFloat(p.netSalary || 0),
                    status: p.isPayslipGenerated ? "Processed" : "Pending",
                    employeeId: employeeData.employeeId,
                    employeeName: employeeData.employeeName,
                    employeePersonalId: p.earnings?.employeePersonalId || userId
                }));
                setPayslips(mappedPayslips);
            } else if (response.statusCode === 200 && (!response.result?.employees || response.result.employees.length === 0)) {
                setPayslips([]);
            } else {
                setError(response.message || "Failed to fetch payslips");
                setPayslips([]);
            }
        } catch (error) {
            setError("Error loading payslips");
            setPayslips([]);
        } finally {
            setLoading(false);
        }
    }, [startMonth, startYear, endMonth, endYear, userId]);

    useEffect(() => {
        fetchPayslips();
    }, [fetchPayslips]);

    const handlePreview = (payslip) => {
        setSelectedPayslip(payslip);
        setPreviewOpen(true);
    };

    const handleClosePreview = () => {
        setPreviewOpen(false);
        setSelectedPayslip(null);
    };

    const generateYearOptions = () => {
        const years = [];
        for (let i = currentYear; i >= currentYear - 5; i--) {
            years.push(i);
        }
        return years;
    };

    return (
        <Box>
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                        <CalendarIcon /> Select Period
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid size={{ xs: 6, md: 2.5 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Start Month</InputLabel>
                                <Select
                                    value={startMonth}
                                    onChange={(e) => setStartMonth(e.target.value)}
                                    label="Start Month"
                                >
                                    {MONTHS.map((month, idx) => (
                                        <MenuItem key={idx} value={idx}>
                                            {month}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid size={{ xs: 6, md: 2.5 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Start Year</InputLabel>
                                <Select
                                    value={startYear}
                                    onChange={(e) => setStartYear(e.target.value)}
                                    label="Start Year"
                                >
                                    {generateYearOptions().map((year) => (
                                        <MenuItem key={year} value={year}>
                                            {year}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid size={{ xs: 6, md: 2.5 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>End Month</InputLabel>
                                <Select
                                    value={endMonth}
                                    onChange={(e) => setEndMonth(e.target.value)}
                                    label="End Month"
                                >
                                    {MONTHS.map((month, idx) => (
                                        <MenuItem key={idx} value={idx}>
                                            {month}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid size={{ xs: 6, md: 2.5 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>End Year</InputLabel>
                                <Select
                                    value={endYear}
                                    onChange={(e) => setEndYear(e.target.value)}
                                    label="End Year"
                                >
                                    {generateYearOptions().map((year) => (
                                        <MenuItem key={year} value={year}>
                                            {year}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 6, md: 2 }}>
                            <Button
                                variant="contained"
                                fullWidth
                                color="primary"
                                onClick={fetchPayslips}
                                disabled={loading}
                                sx={{ textTransform: 'none' }}
                            >
                                {loading ? <CircularProgress size={24} /> : "Fetch Payslips"}
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Error Message */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Payslips Table */}
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Your Payslips
                    </Typography>

                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : payslips.length > 0 ? (
                        <TableContainer component={Paper} variant="outlined">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Month/Year</TableCell>
                                        <TableCell>Employee ID</TableCell>
                                        <TableCell>Gross Salary</TableCell>
                                        <TableCell>Deductions</TableCell>
                                        <TableCell>Net Salary</TableCell>
                                        {/* <TableCell>Status</TableCell> */}
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {payslips.map((payslip, index) => (
                                        <TableRow key={index} hover>
                                            <TableCell>
                                                {MONTHS[payslip.month - 1]} {payslip.year}
                                            </TableCell>
                                            <TableCell>{payslip.employeeId || "N/A"}</TableCell>
                                            <TableCell>₹{payslip.grossSalary?.toLocaleString() || 0}</TableCell>
                                            <TableCell>₹{payslip.totalDeductions?.toLocaleString() || 0}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="bold" color="success.main">
                                                    ₹{payslip.netSalary?.toLocaleString() || 0}
                                                </Typography>
                                            </TableCell>
                                            {/* <TableCell>{getStatusChip(payslip.status)}</TableCell> */}
                                            <TableCell align="center">
                                                <Tooltip title="Preview Payslip">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handlePreview(payslip)}
                                                    >
                                                        <VisibilityIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Alert severity="info">
                            No payslips found for the selected period. Try adjusting your date range.
                        </Alert>
                    )}
                </CardContent>
            </Card>

            {/* Payslip Preview Dialog */}
            {selectedPayslip && (
                <PayslipPreviewView
                    open={previewOpen}
                    onBack={handleClosePreview}
                    data={selectedPayslip}
                />
            )}
        </Box>
    );
};

export default EmployeePayslips;